import { booleanAttribute, ChangeDetectionStrategy, Component, ElementRef, inject, input } from '@angular/core';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'success';

/**
 * Botón real (`<button uiButton>` o `<a uiButton>`), nunca un `div role="button"`.
 *
 * `inactive` en vez de `disabled`: el README pide `aria-disabled`, así el botón sigue en el orden de foco
 * y un lector de pantalla lo anuncia. El clic se corta en la fase de captura, antes de los `(click)` de
 * la pantalla, tanto en `inactive` como en `loading`.
 */
@Component({
  selector: 'button[uiButton], a[uiButton]',
  template: `
    @if (loading()) {
      <span class="spinner" aria-hidden="true"></span>
    }
    <ng-content />
  `,
  styleUrl: './button.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.variant-primary]': 'variant() === "primary"',
    '[class.variant-secondary]': 'variant() === "secondary"',
    '[class.variant-danger]': 'variant() === "danger"',
    '[class.variant-success]': 'variant() === "success"',
    '[class.compact]': 'compact()',
    '[class.block]': 'block()',
    '[class.inactive]': 'inactive()',
    '[attr.aria-disabled]': 'inactive() || loading() ? "true" : null',
    '[attr.aria-busy]': 'loading() ? "true" : null',
  },
})
export class ButtonComponent {
  readonly variant = input<ButtonVariant>('primary');
  readonly compact = input(false, { transform: booleanAttribute });
  readonly block = input(false, { transform: booleanAttribute });
  readonly inactive = input(false, { transform: booleanAttribute });
  readonly loading = input(false, { transform: booleanAttribute });

  constructor() {
    const host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
    host.addEventListener(
      'click',
      (event) => {
        if (this.inactive() || this.loading()) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      { capture: true }
    );
    if (host.tagName === 'BUTTON' && !host.hasAttribute('type')) host.setAttribute('type', 'button');
  }
}
